import { gql } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { PartialDeep } from 'type-fest';

import { CustodianProfileDocument } from 'src/graphql/actions/custodian/custodianProfile.query';

import { apolloClient as client, apolloCache as cache } from '../apollo-client';

import type { ICustodianOperation } from 'src/graphql/schema';
import type { ICustodianProfileQuery } from 'src/graphql/actions/custodian/custodianProfile.query';

const CustodianOperationFragment = gql`
  fragment CustodianOperationFragment on CustodianProfile {
    custodianOperations {
      id
      custodianServiceEvents
      custodianServiceEventsCustom
      dataRetention
      endOfRetentionPeriod
      endOfRetentionPeriodCustom
      operationType
      operationTypeCustom
      regulatoryRequirements
      regulatoryRequirementsCustom
    }
  }
`;

export const removeCustodianOperationCache = (profileId: string, operationId: string) => {
  const cacheData = cloneDeep<ICustodianProfileQuery | null>(
    client.readQuery({
      query: CustodianProfileDocument,
      variables: {
        id: profileId,
      },
    }),
  );

  if (!cacheData?.custodianProfile) return;

  const custodianOperations = cacheData.custodianProfile.custodianOperations.filter(({ id }) => id !== operationId);

  cache.writeQuery({
    query: CustodianProfileDocument,
    variables: {
      id: profileId,
    },
    data: {
      custodianProfile: {
        ...cacheData.custodianProfile,
        custodianOperations,
        policy: {
          ...cacheData.custodianProfile.policy,
          submitForReview: Boolean(custodianOperations.length),
        },
      },
    },
  });
};

export const updateCustodianOperationCache = (operationId: string, data?: PartialDeep<ICustodianOperation>) => {
  if (!data) return;

  client.writeFragment({
    id: `CustodianOperation:${operationId}`,
    fragment: CustodianOperationFragment,
    data,
  });
};

export const addCustodianOperationCache = (profileId: string, data: ICustodianOperation) => {
  const cacheData = cloneDeep<ICustodianProfileQuery | null>(
    client.readQuery({
      query: CustodianProfileDocument,
      variables: {
        id: profileId,
      },
    }),
  );

  if (!cacheData?.custodianProfile) return;

  const custodianOperations = [...cacheData.custodianProfile.custodianOperations, data];

  client.writeQuery({
    query: CustodianProfileDocument,
    variables: {
      id: profileId,
    },
    data: {
      custodianProfile: {
        ...cacheData.custodianProfile,
        custodianOperations,
        policy: {
          ...cacheData.custodianProfile.policy,
          submitForReview: true,
        },
      },
    },
  });
};
